<template>
  <teleport to="#app">
    <div v-if="completedPicture" class="popup-overlay fixed left-0 top-0">
      <div class="flex justify-center absolute inset-0 pointer-events-none">
        <div class="overlay-decor" />
      </div>

      <div class="buttons-wrapper">
        <app-button
          btn-type="primary"
          :btn-text="$t('gallery.claimLater')"
          btn-size="md"
          @click="hideCompletedPicture"
        />

        <app-button
          btn-type="confirm"
          :btn-text="$t('gallery.claim')"
          btn-size="md"
          @click="claimReward"
        />
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="completedPicture"
        class="picture-completed-wrapper flex flex-col items-center justify-center pointer-events-none"
      >
        <div class="text-texts-standard-default text-50 uppercase">
          {{ $t('gallery.congratulations') }}
        </div>

        <div class="font-bold uppercase gradient-text-gold text-72">
          {{ $t('gallery.pictureCompleted') }}
        </div>

        <div
          class="picture-completed-picture mb-4"
          :class="{mobile: $isMobile()}"
        >
          <div
            class="picture-completed-picture-inner"
            :style="{
              backgroundImage: 'url(' + imgPath + ')',
            }"
          />
        </div>

        <div class="picture-completed-rewards-wrapper flex flex-col items-center">
          <main class="flex flex-col mt-4 items-center">
            <div class="picture-completed-rewards-wrapper-title flexing">
              <span class="text-32 text-texts-standard-default font-bold mr-4 uppercase">
                {{ $t(`gallery.${completedPicture.parameters.name}`) }}
              </span>
            </div>

            <p class="text-texts-standard-default text-32 my-6">{{ $t('gallery.reward') }}</p>

            <div class="picture-completed-rewards flexing">
              <reward-box
                v-for="(reward, index) in rewards"
                :key="index"
                :reward-icon="120"
                :reward-icon-name="reward.type"
                :reward-number="reward.value"
              />
            </div>

            <slot />
          </main>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { getIconName } from '@/helpers'
import { mapState, mapActions } from 'pinia'
import { useGalleryPictureStore } from '@/store/pinia/gallery/pictureStore'
import { defineComponent } from 'vue'
import { CATEGORY_BENEFIT } from '@/globalVariables/gameConstants'
import type Reward from '@/interfaces/Reward'
import { pathImages } from '@/globalVariables'

interface ComponentData {
  pathImages: typeof pathImages
}

export default defineComponent({
  name: 'GalleryPackOpened',
  
  data(): ComponentData {
    return {
      pathImages,
    }
  },

  computed: {
    ...mapState(useGalleryPictureStore, ['completedPicture']),

    rewards(): Reward[] {
      return this.completedPicture?.rewards?.map((reward: Reward): Reward => {
        if (reward.type === CATEGORY_BENEFIT) {
          return {
            type: getIconName(reward),
            value: reward.value,
          }
        }
        return reward
      }) || []
    },
    
    imgPath(): string {
      return `${pathImages}gallery/picture-detail/gallery-picture-${this.completedPicture.parameters?.id}.avif`
    },
  },

  methods: {
    ...mapActions(useGalleryPictureStore, ['hideCompletedPicture', 'claimReward'])
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/components/popups.scss';
$pack-types: ('regular', 'jumbo', 'premium');

.popup-overlay {
  background: url($path-images + 'gallery/BG_FULLSCREEN.avif') no-repeat;
  background-size: cover;
}
.picture-completed-wrapper {
  width: 100%;
  min-height: 21.25rem;
  z-index: 25;
  position: fixed;
  inset: 0;

  .picture-completed-picture {
    width: 64.5rem;
    height: 29.5rem;
    // @include background(url('#{$path-gallery}picture-detail/BG_EMOTION_SCREEN_ITEM.avif'), contain);
    z-index: 5;

    &-inner {
      width: 54rem;
      height: 24.75rem;
      background-position: 0 bottom;
      background-size: cover;
      background-repeat: no-repeat;
      position: relative;
      top: 1.85rem;
      left: 7.45rem;
      transform: perspective(29rem) rotateY(5deg);
      border: 0.25rem solid #9ADAFE;
    }

    &.mobile {
      height: 15.5rem;

      .picture-completed-picture-inner {
        width: 31rem;
        height: 14.75rem;
        top: -0.15rem;
        left: 17.45rem;
      }
    }
  }
}

.picture-completed-rewards-wrapper {
  width: 100%;
  min-height: 21.25rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 17.6875rem;
    top: -17.6875rem;
    bottom: 0;
    left: 0;
    right: 0;
    background-image: url($path-gallery + 'picture-detail/BG_EMOTION_SCREEN_EFFECT.avif');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center bottom;
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;

    @if $isWsm {
      background: linear-gradient(to right, transparent, #0b1e37, #154e79, #0b1e37, transparent);
      border-image-source: linear-gradient(to right, transparent, #586b9d, #586b9d, transparent);
      border-image-slice: 1;
      border-width: 0.125rem;
    }

    @if $isSsm {
      background: linear-gradient(
        to right,
        transparent,
        rgba(22, 32, 59, 0.97),
        #605e8f,
        #16203b,
        transparent
      );
      border-image-source: linear-gradient(to right, transparent, #586b9d, #586b9d, transparent);
      border-image-slice: 1;
      border-width: 0.125rem;
    }

    mask-image: linear-gradient(to right, transparent, black 20%, black 80%, transparent 100%);
  }

  &-title {
    padding: 0 10.625rem;
    background-image: linear-gradient(
      90deg,
      rgba(44, 54, 92, 0) 0%,
      rgba(44, 54, 92, 0.8) 15%,
      rgba(44, 54, 92, 0.8) 85%,
      rgba(44, 54, 92, 0) 100%
    );
  }

  header {
    width: 100%;
    height: 5rem;
    background-image: url($path-components + 'progressMessage/BG_TOP_CONTENT.avif');
    background-size: cover;
    background-repeat: no-repeat;
  }

  main {
    width: 100%;
    min-height: 20rem;
  }
  .picture-completed-reward {
    width: 26rem;
    margin: 0 auto;
  }
  :deep() {
    [class^='icon-puzzle'] {
      position: relative;
      left: 0.2rem;
      top: 0.2rem;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  opacity: 1;
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.overlay-decor {
  @include background(url('#{$path-gallery}picture-detail/BG_EMOTION_SCREEN_LIGHT.avif'), contain);
  background-position: top;
  width: 138.813rem;
  height: 60.375rem;
  pointer-events: none;
}
#hide-picture-completed-rewards-wrapper {
  position: absolute;
  bottom: 2.5rem;
  right: 2.5rem;
}
.buttons-wrapper {
  position: absolute;
  bottom: 2.5rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 2.5rem;
  z-index: 5;
}
</style>
